import { startTransition, useEffect, useMemo, useState, type FormEvent } from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type B2cStockBatchCreateRequest,
  type B2cStockBatchDeleteOtpStartResponse,
  type B2cStockBatchRecord,
  type B2cStockKitVariant,
  type B2cStockLifecycleState,
} from "@/lib/api/client"
import {
  getSafetyQrVariant,
  listLaunchableSafetyQrVariants,
  listSafetyQrVariants,
  type SafetyQrVariantDefinition,
} from "@/lib/safetyqr-catalog"
import {
  OperatorEmptyState,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  canAccessStockOpsWorkspace,
  resolveOperatorSession,
  titleizeToken,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

const INPUT_CLASS =
  "w-full rounded-2xl border border-border/70 bg-background/85 px-3 py-2 text-sm shadow-xs outline-none transition-colors focus:border-primary/50"

const SAFETY_QR_VARIANTS = listSafetyQrVariants()
const LAUNCHABLE_SAFETY_QR_VARIANTS = listLaunchableSafetyQrVariants()

type StockLifecycleFilter = B2cStockLifecycleState | "all"
type StockKitFilter = B2cStockKitVariant | "all"

export interface AdminStockOpsSearch {
  kitVariant?: StockKitFilter
  lifecycleState?: StockLifecycleFilter
}

interface AdminStockWorkspaceProps {
  onSearchChange: (next: Partial<AdminStockOpsSearch>) => void
  search: AdminStockOpsSearch
}

interface StockWorkspaceBundle {
  batches: B2cStockBatchRecord[]
  session: OperatorResolvedSession
}

interface CreateBatchFormState {
  kitVariant: B2cStockKitVariant | ""
  notes: string
  quantityKits: string
}

interface DeleteSelectionVerificationState {
  batchSelectionHash: string
  maskedPhone: string
  verificationId: string
}

function createInitialBatchForm(): CreateBatchFormState {
  return {
    kitVariant: LAUNCHABLE_SAFETY_QR_VARIANTS[0]?.kitVariant ?? "",
    notes: "",
    quantityKits: "24",
  }
}

function normalizeToken(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function resolveKitFilter(value: AdminStockOpsSearch["kitVariant"]): StockKitFilter {
  return SAFETY_QR_VARIANTS.some((variant) => variant.kitVariant === value) ? value! : "all"
}

function resolveLifecycleFilter(
  value: AdminStockOpsSearch["lifecycleState"]
): StockLifecycleFilter {
  return value === "generated" || value === "printed" || value === "distributed"
    ? value
    : "all"
}

function getVariantDefinition(kitVariant: string | null | undefined) {
  return kitVariant ? getSafetyQrVariant(kitVariant as B2cStockKitVariant) : null
}

function isLaunchableVariant(variant: SafetyQrVariantDefinition | null) {
  return Boolean(variant?.launchGate.launchable)
}

function getVariantLabel(kitVariant: string | null | undefined) {
  return getVariantDefinition(kitVariant)?.displayName ?? titleizeToken(kitVariant ?? "unknown")
}

function getVariantTagSummary(variant: SafetyQrVariantDefinition | null) {
  if (!variant) {
    return "Catalog metadata unavailable"
  }

  return `${variant.tags.length} tags · ${titleizeToken(variant.productFamily)}`
}

function buildBatchSelectionHash(batchIds: string[]) {
  return batchIds.slice().sort().join("|")
}

function isSuperAdminSession(session: OperatorResolvedSession | null | undefined) {
  if (!session || session.actorType !== "user") {
    return false
  }

  return (
    session.roleToken === "admin"
    && Boolean(session.permissions.viewAllOrganizations)
    && !session.organizationId
  )
}

async function loadStockWorkspaceBundle(
  search: AdminStockOpsSearch
): Promise<StockWorkspaceBundle> {
  const session = await resolveOperatorSession({
    requiredAccess: canAccessStockOpsWorkspace,
  })

  if (!canAccessStockOpsWorkspace(session)) {
    throw new Error("This session does not have access to stock operations.")
  }

  const kitVariant = resolveKitFilter(search.kitVariant)
  const lifecycleState = resolveLifecycleFilter(search.lifecycleState)
  const batches = await apiClient
    .getB2cStockBatches({
      kitVariant: kitVariant === "all" ? undefined : kitVariant,
      lifecycleState: lifecycleState === "all" ? undefined : lifecycleState,
      limit: 50,
    })
    .catch(() => null)

  return {
    batches: batches?.data ?? [],
    session,
  }
}

export function AdminStockWorkspace({
  onSearchChange,
  search,
}: AdminStockWorkspaceProps) {
  const [bundle, setBundle] = useState<StockWorkspaceBundle | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form, setForm] = useState<CreateBatchFormState>(() => createInitialBatchForm())
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([])
  const [deleteVerification, setDeleteVerification] =
    useState<DeleteSelectionVerificationState | null>(null)
  const [deleteOtpCode, setDeleteOtpCode] = useState("")
  const kitFilter = resolveKitFilter(search.kitVariant)
  const lifecycleFilter = resolveLifecycleFilter(search.lifecycleState)
  const selectedVariant = useMemo(
    () => getVariantDefinition(form.kitVariant),
    [form.kitVariant]
  )
  const visibleBatchIds = useMemo(
    () => bundle?.batches.map((batch) => batch.id) ?? [],
    [bundle?.batches]
  )
  const selectedBatchSelectionHash = useMemo(
    () => buildBatchSelectionHash(selectedBatchIds),
    [selectedBatchIds]
  )
  const canGenerateBatches =
    Boolean(form.kitVariant) && isLaunchableVariant(selectedVariant) && !isSubmitting
  const isSuperAdmin = isSuperAdminSession(bundle?.session)
  const allVisibleBatchesSelected =
    visibleBatchIds.length > 0 && visibleBatchIds.every((batchId) => selectedBatchIds.includes(batchId))
  const canRequestDeleteOtp = isSuperAdmin && selectedBatchIds.length > 0 && !isSubmitting
  const canConfirmDeleteSelection = Boolean(
    deleteVerification
    && deleteVerification.batchSelectionHash === selectedBatchSelectionHash
    && deleteOtpCode.trim()
    && !isSubmitting
  )

  useEffect(() => {
    const visibleBatchIdSet = new Set(visibleBatchIds)
    setSelectedBatchIds((previous) => {
      const next = previous.filter((batchId) => visibleBatchIdSet.has(batchId))
      return next.length === previous.length ? previous : next
    })
  }, [visibleBatchIds])

  useEffect(() => {
    if (
      deleteVerification
      && deleteVerification.batchSelectionHash !== selectedBatchSelectionHash
    ) {
      setDeleteVerification(null)
      setDeleteOtpCode("")
    }
  }, [deleteVerification, selectedBatchSelectionHash])

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadStockWorkspaceBundle(search)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the SafetyQR stock workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [search])

  async function refreshWorkspace() {
    const nextBundle = await loadStockWorkspaceBundle(search)

    startTransition(() => {
      setBundle(nextBundle)
      setErrorMessage(null)
    })
  }

  async function handleCreateBatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!form.kitVariant || !isLaunchableVariant(selectedVariant)) {
      setErrorMessage("No launch-ready SafetyQR kit variants are available for generation.")
      return
    }

    const quantityKits = Number.parseInt(form.quantityKits, 10)

    if (!Number.isFinite(quantityKits) || quantityKits < 1 || quantityKits > 500) {
      setErrorMessage("Quantity of kits must be between 1 and 500.")
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const payload: B2cStockBatchCreateRequest = {
        kitVariant: form.kitVariant,
        notes: form.notes.trim() || null,
        quantityKits,
      }

      await apiClient.createB2cStockBatch(payload)
      startTransition(() => {
        setForm(createInitialBatchForm())
      })
      await refreshWorkspace()
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to create the SafetyQR kit batch."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleOpenPrintPreview(batchId: string) {
    setErrorMessage(null)

    try {
      window.open(
        `/admin/stock-ops/print?batchId=${encodeURIComponent(batchId)}&returnTo=${encodeURIComponent("/admin/stock-ops")}`,
        "_blank",
        "noopener"
      )
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to prepare the SafetyQR print preview."
      )
    }
  }

  async function handleDistribute(batchId: string) {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      await apiClient.distributeB2cStockBatch(batchId)
      await refreshWorkspace()
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to mark the kit batch as distributed."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  function applyDeleteOtpResponse(
    response: B2cStockBatchDeleteOtpStartResponse,
    batchIds: string[]
  ) {
    setDeleteVerification({
      batchSelectionHash: buildBatchSelectionHash(batchIds),
      maskedPhone: response.maskedPhone || "configured super-admin phone",
      verificationId: response.verificationId,
    })
    setDeleteOtpCode("")
  }

  function toggleBatchSelection(batchId: string, checked: boolean) {
    setSelectedBatchIds((previous) => {
      if (checked) {
        return previous.includes(batchId) ? previous : [...previous, batchId]
      }

      return previous.filter((candidate) => candidate !== batchId)
    })
  }

  function handleToggleAllVisibleBatches() {
    setSelectedBatchIds((previous) => {
      if (allVisibleBatchesSelected) {
        const visible = new Set(visibleBatchIds)
        return previous.filter((batchId) => !visible.has(batchId))
      }

      return Array.from(new Set([...previous, ...visibleBatchIds]))
    })
  }

  async function handleStartDeleteOtp() {
    if (!selectedBatchIds.length) {
      setErrorMessage("Select at least one batch before requesting OTP.")
      return
    }

    const sortedSelection = selectedBatchIds.slice().sort()
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const response = await apiClient.startB2cStockBatchDeleteOtp({
        batchIds: sortedSelection,
      })
      applyDeleteOtpResponse(response.data, sortedSelection)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to request OTP for selected batches."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDeleteSelectedBatches() {
    if (!deleteVerification) {
      setErrorMessage("Request OTP before confirming batch deletion.")
      return
    }

    const sortedSelection = selectedBatchIds.slice().sort()
    const expectedSelectionHash = buildBatchSelectionHash(sortedSelection)

    if (expectedSelectionHash !== deleteVerification.batchSelectionHash) {
      setErrorMessage("Selection changed. Request a new OTP for the updated selection.")
      return
    }

    if (!deleteOtpCode.trim()) {
      setErrorMessage("Enter the OTP code to confirm deletion.")
      return
    }

    const confirmed = window.confirm(
      `Delete ${sortedSelection.length} selected batches from production? This cannot be undone.`
    )
    if (!confirmed) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      await apiClient.deleteSelectedB2cStockBatches({
        batchIds: sortedSelection,
        otp: deleteOtpCode.trim(),
        verificationId: deleteVerification.verificationId,
      })
      setDeleteVerification(null)
      setDeleteOtpCode("")
      setSelectedBatchIds([])
      await refreshWorkspace()
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to delete selected batches after OTP verification."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <StockWorkspaceLoadingState /> : null}

      {!isLoading && !bundle && !errorMessage ? (
        <OperatorPanel
          title="Stock access restricted"
          description="This page is only available to sessions with stock operations access."
        >
          <OperatorStatusMessage tone="danger">
            Sign in again with an admin account to open stock operations.
          </OperatorStatusMessage>
        </OperatorPanel>
      ) : null}

      {!isLoading && bundle ? (
        <>
          <div className="grid gap-5 xl:grid-cols-[minmax(0,0.98fr)_minmax(0,1.02fr)]">
            <OperatorPanel
              title="Generate a kit batch"
              description="The create flow now issues ProductKit and PhysicalTag rows only for launch-ready variants from the shared SafetyQR packaging catalog."
            >
              <form className="grid gap-4" onSubmit={handleCreateBatch}>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Kit variant</span>
                  <select
                    aria-label="Kit variant"
                    className={INPUT_CLASS}
                    disabled={!LAUNCHABLE_SAFETY_QR_VARIANTS.length}
                    value={form.kitVariant}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        kitVariant: event.target.value as B2cStockKitVariant | "",
                      }))
                    }
                  >
                    {LAUNCHABLE_SAFETY_QR_VARIANTS.length ? (
                      LAUNCHABLE_SAFETY_QR_VARIANTS.map((variant) => (
                        <option key={variant.kitVariant} value={variant.kitVariant}>
                          {variant.displayName}
                        </option>
                      ))
                    ) : (
                      <option value="">No launch-ready variants available</option>
                    )}
                  </select>
                </label>
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      {selectedVariant?.displayName ?? "Unknown variant"}
                    </span>
                    <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                      {getVariantTagSummary(selectedVariant)}
                    </span>
                    <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                      {titleizeToken(selectedVariant?.launchGate.status ?? "unknown")}
                    </span>
                  </div>
                  <p className="mt-3 leading-6 text-muted-foreground">
                    {selectedVariant?.launchGate.note ??
                      (LAUNCHABLE_SAFETY_QR_VARIANTS.length
                        ? "The selected kit variant does not expose catalog notes."
                        : "Batch generation stays disabled until at least one catalog variant reaches launch_ready.")}
                  </p>
                </div>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Quantity of kits</span>
                  <input
                    aria-label="Quantity of kits"
                    className={INPUT_CLASS}
                    inputMode="numeric"
                    value={form.quantityKits}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        quantityKits: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Ops notes</span>
                  <textarea
                    aria-label="Ops notes"
                    className={`${INPUT_CLASS} min-h-28 resize-y`}
                    value={form.notes}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        notes: event.target.value,
                      }))
                    }
                  />
                </label>
                <div className="flex flex-wrap gap-3">
                  <Button disabled={!canGenerateBatches} type="submit">
                    {isSubmitting ? "Generating…" : "Generate kit batch"}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={() => setForm(createInitialBatchForm())}
                  >
                    Reset form
                  </Button>
                </div>
              </form>
            </OperatorPanel>

            <OperatorPanel
              title="Stock filters"
              description="Filter the batch list by lifecycle state and kit variant."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Lifecycle</span>
                  <select
                    className={INPUT_CLASS}
                    value={lifecycleFilter}
                    onChange={(event) =>
                      onSearchChange({
                        lifecycleState: event.target.value as StockLifecycleFilter,
                      })
                    }
                  >
                    <option value="all">All</option>
                    <option value="generated">Generated</option>
                    <option value="printed">Printed</option>
                    <option value="distributed">Distributed</option>
                  </select>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Kit variant</span>
                  <select
                    className={INPUT_CLASS}
                    value={kitFilter}
                    onChange={(event) =>
                      onSearchChange({
                        kitVariant: event.target.value as StockKitFilter,
                      })
                    }
                  >
                    <option value="all">All variants</option>
                    {SAFETY_QR_VARIANTS.map((variant) => (
                      <option key={variant.kitVariant} value={variant.kitVariant}>
                        {variant.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </OperatorPanel>
          </div>

          <OperatorPanel
            title="Batch activity"
            description="Open the print preview to review metadata first. The explicit print approval inside the preview transitions generated batches to printed."
          >
            {isSuperAdmin ? (
              <div className="mb-4 rounded-3xl border border-destructive/25 bg-destructive/5 px-4 py-4">
                <p className="text-sm font-medium text-destructive">
                  Super admin destructive controls
                </p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Select one or more batches, request OTP verification, then confirm permanent
                  deletion from production.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    disabled={!visibleBatchIds.length || isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={handleToggleAllVisibleBatches}
                  >
                    {allVisibleBatchesSelected ? "Unselect visible" : "Select all visible"}
                  </Button>
                  <Button
                    disabled={!selectedBatchIds.length || isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedBatchIds([])
                      setDeleteVerification(null)
                      setDeleteOtpCode("")
                    }}
                  >
                    Clear selection
                  </Button>
                  <Button
                    disabled={!canRequestDeleteOtp}
                    type="button"
                    variant="destructive"
                    onClick={handleStartDeleteOtp}
                  >
                    {isSubmitting ? "Requesting OTP…" : `Request OTP (${selectedBatchIds.length})`}
                  </Button>
                </div>

                {deleteVerification ? (
                  <div className="mt-4 grid gap-3 rounded-2xl border border-border/70 bg-background/80 px-3 py-3">
                    <p className="text-sm text-muted-foreground">
                      Enter the OTP sent to <strong>{deleteVerification.maskedPhone}</strong> to
                      delete {selectedBatchIds.length} selected batches.
                    </p>
                    <input
                      aria-label="Delete batches OTP code"
                      autoComplete="one-time-code"
                      className={INPUT_CLASS}
                      inputMode="numeric"
                      placeholder="Enter OTP"
                      value={deleteOtpCode}
                      onChange={(event) => setDeleteOtpCode(event.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={!canConfirmDeleteSelection}
                        type="button"
                        variant="destructive"
                        onClick={handleDeleteSelectedBatches}
                      >
                        {isSubmitting ? "Deleting batches…" : "Delete selected batches"}
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        type="button"
                        variant="outline"
                        onClick={handleStartDeleteOtp}
                      >
                        Resend OTP
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {bundle.batches.length ? (
              <div className="grid gap-3">
                {bundle.batches.map((batch) => {
                  const distributed = normalizeToken(batch.lifecycleState) === "distributed"
                  const variant = getVariantDefinition(batch.kitVariant)
                  const selected = selectedBatchIds.includes(batch.id)
                  return (
                    <div
                      key={batch.id}
                      className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                    >
                      <div className="flex items-start gap-3">
                        {isSuperAdmin ? (
                          <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                            <input
                              aria-label={`Select batch ${batch.batchCode}`}
                              checked={selected}
                              disabled={isSubmitting}
                              type="checkbox"
                              onChange={(event) =>
                                toggleBatchSelection(batch.id, event.currentTarget.checked)
                              }
                            />
                            Select
                          </label>
                        ) : null}
                        <div className="flex flex-1 flex-wrap items-start justify-between gap-3">
                          <div className="max-w-3xl">
                            <div className="flex flex-wrap items-center gap-2">
                              <strong>{batch.batchCode}</strong>
                              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                                {getVariantLabel(batch.kitVariant)}
                              </span>
                              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                                {titleizeToken(batch.lifecycleState)}
                              </span>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">
                              {(batch.quantityKits ?? batch.kitCount ?? 0) || 0} kits
                              {typeof batch.tagCount === "number" ? ` · ${batch.tagCount} printed tags` : ""}
                              {batch.firstKitCode ? ` · ${batch.firstKitCode}` : ""}
                              {batch.lastKitCode && batch.lastKitCode !== batch.firstKitCode
                                ? ` to ${batch.lastKitCode}`
                                : ""}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">
                              {batch.organization?.name || "Portal scope"} · created{" "}
                              {formatDateTime(batch.createdAt)} · printed{" "}
                              {formatDateTime(batch.printedAt)} · distributed{" "}
                              {formatDateTime(batch.distributedAt)}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">
                              {variant?.launchGate.note ?? "No catalog note available for this variant."}
                            </p>
                            <p className="mt-2 text-sm text-muted-foreground">
                              {batch.notes || "No ops notes recorded for this batch."}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              disabled={isSubmitting}
                              type="button"
                              onClick={() => handleOpenPrintPreview(batch.id)}
                            >
                              Open print preview
                            </Button>
                            <Button
                              disabled={distributed || isSubmitting}
                              type="button"
                              variant="outline"
                              onClick={() => handleDistribute(batch.id)}
                            >
                              {distributed ? "Distributed" : "Mark distributed"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <OperatorEmptyState
                title="No kit batches matched the current scope."
                message="No generated, printed, or distributed kit batches are available in this filter."
              />
            )}
          </OperatorPanel>
        </>
      ) : null}
    </>
  )
}

function StockWorkspaceLoadingState() {
  return (
    <>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.98fr)_minmax(0,1.02fr)]">
        <OperatorPanel
          title="Generate a kit batch"
          description="The create flow now issues ProductKit and PhysicalTag rows only for launch-ready variants from the shared SafetyQR packaging catalog."
        >
          <div className="grid gap-4">
            <Skeleton className="h-11 w-full rounded-2xl" />
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-7 w-32 rounded-full" />
                <Skeleton className="h-7 w-28 rounded-full" />
                <Skeleton className="h-7 w-24 rounded-full" />
              </div>
              <Skeleton className="mt-3 h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-5/6" />
            </div>
            <Skeleton className="h-11 w-full rounded-2xl" />
            <Skeleton className="h-28 w-full rounded-2xl" />
            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-10 w-36 rounded-full" />
              <Skeleton className="h-10 w-28 rounded-full" />
            </div>
          </div>
        </OperatorPanel>

        <OperatorPanel
          title="Stock filters"
          description="Filter the batch list by lifecycle state and kit variant."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-11 w-full rounded-2xl" />
            <Skeleton className="h-11 w-full rounded-2xl" />
          </div>
        </OperatorPanel>
      </div>

      <OperatorPanel
        title="Batch activity"
        description="Open the print preview to review metadata first. The explicit print approval inside the preview transitions generated batches to printed."
      >
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`stock-loading-batch-${index}`}
              className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-6 w-28 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                  <Skeleton className="mt-3 h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-5/6" />
                  <Skeleton className="mt-2 h-4 w-4/5" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Skeleton className="h-10 w-36 rounded-full" />
                  <Skeleton className="h-10 w-36 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </OperatorPanel>
    </>
  )
}
